import queryString from 'query-string'
import { useMemo } from 'react'
import { useHistory, useLocation } from 'react-router'
import { getStringQueryParam } from 'src/navigation/queryParams'
import { type ModalLayer, toLayerParamKey } from 'src/navigation/reservedParams'

// Re-exported so consumers can import the whole convention from one module; the
// pure (routing-free) definitions live in `reservedParams.ts`.
export {
  isReservedModalParam,
  MODAL_LAYERS,
  preserveReservedParams,
  RESERVED_MODAL_PARAM,
  toLayerParamKey,
} from 'src/navigation/reservedParams'
export type { ModalLayer } from 'src/navigation/reservedParams'

// Modal-routing convention (EX-39749 / EX-39962).
//
// Overlays are expressed as reserved query params on the parent page, replacing
// the connected-react-router background-location model. The path is always a
// real page; the overlay is a param on it; the "background" is implicit (the
// path). Two independent, stackable layers:
//
//   ?modal=<name>     centered / popup layer (renders over everything)
//   ?flyout=<name>    drawer layer (left/right flyout)
//
// Each layer owns a camelCase param namespace for anything the overlay needs:
//
//   ?modal=deactivate&modalId=vc_123        modalId         -> params.id
//   ?flyout=invite&flyoutFirstName=Sam      flyoutFirstName -> params.firstName
//
// The bare key is the overlay identity; `<layer><CamelKey>` params are its data.
// The whole `^(modal|flyout)[A-Z]...` namespace is reserved so the filter engine
// never strips it (see `preserveReservedParams`).
//
// The URL holds SHAREABLE, NON-SECRET scalars only (id, step, tab). Secrets and
// rich/transient payloads belong in the modal context, never the URL.

const decapitalize = (s: string): string =>
  s.charAt(0).toLowerCase() + s.slice(1)

// A `<layer><CamelKey>` data param for this layer (excludes the bare layer key).
const isLayerDataParam = (key: string, layer: ModalLayer): boolean =>
  key.length > layer.length &&
  key.startsWith(layer) &&
  key[layer.length] === key[layer.length].toUpperCase() &&
  key[layer.length] !== key[layer.length].toLowerCase()

// The bare layer key OR one of its data params.
const belongsToLayer = (key: string, layer: ModalLayer): boolean =>
  key === layer || isLayerDataParam(key, layer)

type ParsedQuery = ReturnType<typeof queryString.parse>

export interface OverlayRouteControls {
  // The open overlay's name for this layer, or undefined if none is open.
  name?: string
  // Whether any overlay is open on this layer.
  isOpen: boolean
  // The layer's data params, de-prefixed: `modalId=x` -> `{ id: 'x' }`.
  // Reads of an absent param return `undefined` at runtime, so the value type
  // is `string | undefined` to keep consumers honest about the missing case.
  params: Record<string, string | undefined>
  // The location that opening `name` would navigate to — for a `<Link to>` so
  // a trigger can stay a link (vs the imperative open()). Preserves the other
  // layer + filters present in the current URL.
  getOpenLocation: (
    name: string,
    params?: Record<string, string | undefined>,
  ) => { pathname: string; search: string }
  // Open `name` with optional data params. Preserves the other layer + filters.
  open: (name: string, params?: Record<string, string | undefined>) => void
  // Close this layer (drop its params). Preserves the other layer + filters.
  close: () => void
}

/**
 * Read/control one overlay layer (`modal` or `flyout`) via the URL.
 *
 * Opening pushes a new history entry, so the browser Back button closes the
 * overlay before leaving the parent page. Closing drops only this layer's
 * params, leaving the other layer and all page filters intact — the parent
 * page never unmounts.
 */
export const useOverlayRoute = (layer: ModalLayer): OverlayRouteControls => {
  const history = useHistory()
  const { search, pathname } = useLocation()

  return useMemo(() => {
    const query = queryString.parse(search)
    const name = getStringQueryParam(query[layer])

    const params: Record<string, string> = {}
    Object.keys(query).forEach((key) => {
      if (!isLayerDataParam(key, layer)) return
      const value = getStringQueryParam(query[key])
      if (value !== undefined) {
        params[decapitalize(key.slice(layer.length))] = value
      }
    })

    // Strip this layer's bare key + data params; keep the other layer + filters.
    const withoutLayer = (q: ParsedQuery): ParsedQuery => {
      const out: ParsedQuery = {}
      Object.keys(q).forEach((key) => {
        if (!belongsToLayer(key, layer)) {
          out[key] = q[key]
        }
      })
      return out
    }

    const getOpenLocation = (
      nextName: string,
      nextParams: Record<string, string | undefined> = {},
    ): { pathname: string; search: string } => {
      // Read the live location (not the render-time snapshot) so an open/close
      // fired from an async callback uses the current URL.
      const current = history.location
      const merged: ParsedQuery = {
        ...withoutLayer(queryString.parse(current.search)),
        [layer]: nextName,
      }
      Object.entries(nextParams).forEach(([key, value]) => {
        if (value !== undefined) {
          merged[toLayerParamKey(layer, key)] = value
        }
      })
      return {
        pathname: current.pathname,
        search: queryString.stringify(merged),
      }
    }

    const open = (
      nextName: string,
      nextParams: Record<string, string | undefined> = {},
    ): void => {
      history.push(getOpenLocation(nextName, nextParams))
    }

    const close = (): void => {
      // replace (not push): dropping the layer's params should not add a history
      // entry, so Back returns to the page state before the overlay opened
      // instead of stepping back onto the still-open modal URL.
      const current = history.location
      history.replace({
        pathname: current.pathname,
        search: queryString.stringify(
          withoutLayer(queryString.parse(current.search)),
        ),
      })
    }

    return {
      name,
      isOpen: name !== undefined,
      params,
      getOpenLocation,
      open,
      close,
    }
  }, [history, pathname, search, layer])
}

// Convenience accessors for the two layers.
export const useModalRoute = (): OverlayRouteControls =>
  useOverlayRoute('modal')
export const useFlyoutRoute = (): OverlayRouteControls =>
  useOverlayRoute('flyout')
